import { omitKeys, subsetTypeHasKey, toLocalDateString } from "@hex-insights/core";
import { FormState, formStateHasField } from "@hex-insights/forms";
import { EmployeeFilterInput, mergeFilterInputs } from "../../../Utilities";
import * as EmployeeFilterFormValues from "./form-values";

export function countActiveStandardFilters<
	K extends keyof EmployeeFilterFormValues.FilterFormValues = keyof EmployeeFilterFormValues.FilterFormValues,
>(formValues: Pick<EmployeeFilterFormValues.FilterFormValues, K>) {
	let numActive = 0;
	if (subsetTypeHasKey(formValues, "statusEQ") && formValues.statusEQ) {
		numActive++;
	}
	if (subsetTypeHasKey(formValues, "roleEQ") && formValues.roleEQ) {
		numActive++;
	}
	if (subsetTypeHasKey(formValues, "academicSubjectIDsIn") && formValues.academicSubjectIDsIn.length > 0) {
		numActive++;
	}
	if (subsetTypeHasKey(formValues, "classYearIDsIn") && formValues.classYearIDsIn.length > 0) {
		numActive++;
	}
	return numActive;
}

export function clearStandardFilters<
	K extends keyof EmployeeFilterFormValues.FilterFormValues = keyof EmployeeFilterFormValues.FilterFormValues,
>(formState: FormState<Pick<EmployeeFilterFormValues.FilterFormValues, K>>) {
	if (formStateHasField(formState, "statusEQ")) {
		formState.formClearFunctions.statusEQ();
	}
	if (formStateHasField(formState, "roleEQ")) {
		formState.formClearFunctions.roleEQ();
	}
	if (formStateHasField(formState, "academicSubjectIDsIn")) {
		formState.formClearFunctions.academicSubjectIDsIn();
	}
	if (formStateHasField(formState, "classYearIDsIn")) {
		formState.formClearFunctions.classYearIDsIn();
	}
}

export function resetStandardFilters<
	K extends keyof EmployeeFilterFormValues.FilterFormValues = keyof EmployeeFilterFormValues.FilterFormValues,
>(
	formState: FormState<Pick<EmployeeFilterFormValues.FilterFormValues, K>>,
	initial: EmployeeFilterFormValues.FilterFormValues,
) {
	if (formStateHasField(formState, "statusEQ")) {
		formState.formSetFunctions.statusEQ(initial.statusEQ);
	}
	if (formStateHasField(formState, "roleEQ")) {
		formState.formSetFunctions.roleEQ(initial.roleEQ);
	}
	if (formStateHasField(formState, "academicSubjectIDsIn")) {
		formState.formSetFunctions.academicSubjectIDsIn(initial.academicSubjectIDsIn);
	}
	if (formStateHasField(formState, "classYearIDsIn")) {
		formState.formSetFunctions.classYearIDsIn(initial.classYearIDsIn);
	}
}

export function prepareFormValuesForConversion<
	K extends keyof EmployeeFilterFormValues.FilterFormValues = keyof EmployeeFilterFormValues.FilterFormValues,
>(formValues: Pick<EmployeeFilterFormValues.FilterFormValues, K>) {
	return omitKeys(formValues, ["statusEQ", "academicSubjectIDsIn", "classYearIDsIn"]) as Pick<
		EmployeeFilterFormValues.FilterFormValues,
		Exclude<K, "statusEQ" | "academicSubjectIDsIn" | "classYearIDsIn">
	>;
}

export function toFilterInputs<
	K extends keyof EmployeeFilterFormValues.FilterFormValues = keyof EmployeeFilterFormValues.FilterFormValues,
>(
	formValues: Pick<EmployeeFilterFormValues.FilterFormValues, K>,
	filterInput: EmployeeFilterInput,
): EmployeeFilterInput {
	const customFilterInputs: EmployeeFilterInput[] = [];

	if (subsetTypeHasKey(formValues, "statusEQ") && formValues.statusEQ) {
		const today = toLocalDateString(new Date());

		switch (formValues.statusEQ) {
			case "Current":
				customFilterInputs.push({
					startDateLTE: today,
					or: [{ endDateIsNull: true }, { endDateGTE: today }],
				});
				break;
			case "Former":
				customFilterInputs.push({
					endDateLT: today,
				});
				break;
			case "Future":
				customFilterInputs.push({
					startDateGT: today,
				});
				break;
		}
	}

	if (subsetTypeHasKey(formValues, "academicSubjectIDsIn") && formValues.academicSubjectIDsIn.length > 0) {
		customFilterInputs.push({
			teacherCourseSectionEnrollments: [
				{
					courseSection: [
						{
							academicTerm: [
								{ startDateLTE: toLocalDateString(new Date()), endDateGTE: toLocalDateString(new Date()) },
							],
							course: [{ academicSubjectIDIn: formValues.academicSubjectIDsIn }],
						},
					],
				},
			],
		});
	}

	if (subsetTypeHasKey(formValues, "classYearIDsIn") && formValues.classYearIDsIn.length > 0) {
		customFilterInputs.push({
			or: [
				{
					teacherCourseSectionEnrollments: [
						{
							courseSection: [
								{
									academicTerm: [
										{ startDateLTE: toLocalDateString(new Date()), endDateGTE: toLocalDateString(new Date()) },
									],
									course: [{ classYears: [{ idIn: formValues.classYearIDsIn }] }],
								},
							],
						},
					],
				},
				{
					teacherHomeRoomSectionEnrollments: [
						{
							homeRoomSection: [
								{
									academicTerm: [
										{ startDateLTE: toLocalDateString(new Date()), endDateGTE: toLocalDateString(new Date()) },
									],
									homeRoom: [{ classYearIDIn: formValues.classYearIDsIn }],
								},
							],
						},
					],
				},
			],
		});
	}

	return mergeFilterInputs(filterInput, ...customFilterInputs);
}
